
"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  BarChart3,
  Bot,
  ChevronDown,
  LoaderCircle,
  Menu,
  MoreVertical,
  Plus,
  Send,
  Settings2,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

type Row = Record<string, any>;
type Conversation = { conversation_id: string; title: string; updated_at: string };
type Message = { id: string; role: string; content: string; created_at: string };
type WorkspaceSurface = "conversation" | "statistics" | "manage";
type StatsScope = "SELECTED_BOT" | "ALL_BOTS";

const AUTO_MODEL = "__AUTO__";
const BOT_RENDERER_CONTRACT = "RUNYI_BOTS_WORKSPACE_RENDERER_V1";

function stripBotProfile(text: string, role: string) {
  return role === "user"
    ? text.replace(/^<RUNYI_BOT_PROFILE>[\s\S]*?<\/RUNYI_BOT_PROFILE>\s*/i, "")
    : text;
}

function formatMoney(value: unknown) {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount) ? `$${amount.toFixed(6)}` : "$0.000000";
}

export function RunyiBotWorkspacePanel({
  payload,
  supabase,
  connected,
  isAdmin,
}: {
  payload: Row | null;
  supabase: SupabaseClient | null;
  connected: boolean;
  isAdmin: boolean;
}) {
  const canonicalCatalogue = payload?.bot_configuration_catalogue && typeof payload.bot_configuration_catalogue === "object"
    ? payload.bot_configuration_catalogue as Row
    : null;
  const canonicalWorkspaceState = payload?.workspace_state && typeof payload.workspace_state === "object"
    ? payload.workspace_state as Row
    : null;
  const canonicalStatistics = payload?.bot_statistics && typeof payload.bot_statistics === "object"
    ? payload.bot_statistics as Row
    : null;

  const [catalogue, setCatalogue] = useState<Row | null>(canonicalCatalogue);
  const [actorId, setActorId] = useState<string | null>(null);
  const [botKey, setBotKey] = useState(() => String(canonicalWorkspaceState?.selected_bot_key ?? ""));
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(() =>
    canonicalWorkspaceState?.selected_conversation_id ? String(canonicalWorkspaceState.selected_conversation_id) : null,
  );
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [surface, setSurface] = useState<WorkspaceSurface>("conversation");
  const [statsScope, setStatsScope] = useState<StatsScope>("SELECTED_BOT");
  const [statistics, setStatistics] = useState<Row | null>(canonicalStatistics);
  const [configOpen, setConfigOpen] = useState(false);
  const [createBotOpen, setCreateBotOpen] = useState(false);
  const [rowMenu, setRowMenu] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const bots = useMemo(() => (Array.isArray(catalogue?.bots) ? catalogue!.bots as Row[] : []), [catalogue]);
  const models = useMemo(() => (Array.isArray(catalogue?.models) ? catalogue!.models as Row[] : []), [catalogue]);
  const providers = useMemo(() => (Array.isArray(catalogue?.providers) ? catalogue!.providers as Row[] : []), [catalogue]);
  const bot = useMemo(
    () => bots.find((item) => item.bot_key === botKey) ?? bots.find((item) => item.active !== false) ?? null,
    [bots, botKey],
  );
  const provider = String(bot?.provider_key ?? catalogue?.defaults?.provider_key ?? "OPENAI");
  const automaticModel = String(bot?.model_policy ?? "CHEAPEST_ELIGIBLE_MODEL") === "CHEAPEST_ELIGIBLE_MODEL";
  const modelValue = automaticModel ? AUTO_MODEL : String(bot?.model_key ?? AUTO_MODEL);
  const providerModels = models.filter((item) => String(item.provider_key) === provider);
  const selectedStats = useMemo(
    () => (Array.isArray(statistics?.bots) ? statistics!.bots as Row[] : []).find((item) => item.bot_key === botKey) ?? null,
    [statistics, botKey],
  );

  const acceptCatalogue = useCallback((next: Row | null | undefined, preferred?: string) => {
    if (!next) return;
    setCatalogue(next);
    const nextBots = Array.isArray(next.bots) ? next.bots as Row[] : [];
    const wanted = preferred ?? botKey;
    const resolved = nextBots.some((item) => item.bot_key === wanted && item.active !== false)
      ? wanted
      : String(nextBots.find((item) => item.active !== false)?.bot_key ?? "");
    setBotKey(resolved);
  }, [botKey]);

  const loadIdentityAndState = useCallback(async () => {
    if (!supabase || !connected) return;
    const { data: sessionData } = await supabase.auth.getSession();
    const uid = sessionData.session?.user?.id ?? null;
    setActorId(uid);
    if (!uid) return;

    if (canonicalWorkspaceState) {
      const savedBot = String(canonicalWorkspaceState.selected_bot_key ?? "");
      const savedConversation = canonicalWorkspaceState.selected_conversation_id
        ? String(canonicalWorkspaceState.selected_conversation_id)
        : null;
      if (savedBot) setBotKey(savedBot);
      if (savedConversation) setConversationId(savedConversation);
      return;
    }

    const { data } = await (supabase as any).rpc("runyi_service_workspace_ui_state_read", {
      p_actor_user_id: uid,
      p_workspace_type: "BOT",
    });
    const savedBot = String(data?.selected_bot_key ?? "");
    const savedConversation = data?.selected_conversation_id ? String(data.selected_conversation_id) : null;
    if (savedBot) setBotKey(savedBot);
    if (savedConversation) setConversationId(savedConversation);
  }, [supabase, connected, canonicalWorkspaceState]);

  const persistState = useCallback(async (nextBot: string | null, nextConversation: string | null) => {
    if (!supabase || !actorId) return;
    await (supabase as any).rpc("runyi_service_workspace_ui_state_set", {
      p_actor_user_id: actorId,
      p_workspace_type: "BOT",
      p_selected_bot_key: nextBot,
      p_selected_conversation_id: nextConversation,
      p_state_patch: { renderer_contract: BOT_RENDERER_CONTRACT },
    });
  }, [supabase, actorId]);

  const loadCatalogue = useCallback(async () => {
    if (!supabase || !connected) return;
    const { data, error } = await supabase.functions.invoke<Row>("runyi-bot-chat", { body: { action: "catalogue" } });
    if (error || data?.error) {
      setNotice(String(data?.error ?? error?.message ?? "RUNYI Bot catalogue is unavailable."));
      return;
    }
    acceptCatalogue(data);
  }, [supabase, connected, acceptCatalogue]);

  const loadStatistics = useCallback(async () => {
    if (!supabase || !actorId) return;
    const { data, error } = await (supabase as any).rpc("runyi_service_bot_statistics", {
      p_actor_user_id: actorId,
      p_bot_key: null,
    });
    if (!error && data) setStatistics(data as Row);
  }, [supabase, actorId]);

  const loadHistory = useCallback(async () => {
    if (!supabase || !connected || !actorId || !botKey) {
      setConversations([]);
      return;
    }
    const { data, error } = await (supabase as any).rpc("runyi_service_workspace_conversation_catalogue", {
      p_actor_user_id: actorId,
      p_workspace_type: "BOT",
      p_bot_key: botKey,
      p_limit: 100,
    });
    if (error) {
      setNotice(error.message);
      return;
    }
    setConversations((Array.isArray(data?.items) ? data.items : []) as Conversation[]);
  }, [supabase, connected, actorId, botKey]);

  const loadMessages = useCallback(async (id: string) => {
    if (!supabase || !connected) return;
    const { data, error } = await supabase
      .from("ai_messages")
      .select("id,role,content,created_at")
      .eq("conversation_id", id)
      .order("created_at", { ascending: true });
    if (error) {
      setNotice(error.message);
      return;
    }
    setMessages((data ?? []) as Message[]);
  }, [supabase, connected]);

  useEffect(() => {
    if (!canonicalCatalogue) return;
    acceptCatalogue(canonicalCatalogue, String(canonicalWorkspaceState?.selected_bot_key ?? ""));
  }, [canonicalCatalogue, canonicalWorkspaceState?.selected_bot_key, acceptCatalogue]);
  useEffect(() => {
    if (!canonicalWorkspaceState) return;
    const savedBot = String(canonicalWorkspaceState.selected_bot_key ?? "");
    const savedConversation = canonicalWorkspaceState.selected_conversation_id
      ? String(canonicalWorkspaceState.selected_conversation_id)
      : null;
    if (savedBot) setBotKey(savedBot);
    if (savedConversation) setConversationId(savedConversation);
  }, [canonicalWorkspaceState]);
  useEffect(() => {
    if (canonicalStatistics) setStatistics(canonicalStatistics);
  }, [canonicalStatistics]);
  useEffect(() => { void loadIdentityAndState(); }, [loadIdentityAndState]);
  useEffect(() => { if (!canonicalCatalogue) void loadCatalogue(); }, [canonicalCatalogue, loadCatalogue]);
  useEffect(() => { if (actorId && !canonicalStatistics) void loadStatistics(); }, [actorId, canonicalStatistics, loadStatistics]);
  useEffect(() => {
    if (!botKey) return;
    setConversationId(null);
    setMessages([]);
    setDraft("");
    setRowMenu(null);
    void loadHistory();
    void persistState(botKey, null);
  }, [botKey, loadHistory, persistState]);
  useEffect(() => {
    if (!conversationId) return;
    void loadMessages(conversationId);
    void persistState(botKey || null, conversationId);
  }, [conversationId, botKey, loadMessages, persistState]);

  function newChat() {
    setConversationId(null);
    setMessages([]);
    setDraft("");
    setSurface("conversation");
    setDrawerOpen(false);
    setRowMenu(null);
    setNotice(null);
    void persistState(botKey || null, null);
  }

  async function send() {
    const text = draft.trim();
    if (!supabase || !botKey || !text || busy) return;
    setBusy(true);
    setDraft("");
    setNotice(null);
    const optimistic: Message = {
      id: `local-${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((current) => [...current, optimistic]);
    const { data, error } = await supabase.functions.invoke<Row>("runyi-bot-chat", {
      body: { action: "chat", bot_key: botKey, message: text, conversation_id: conversationId },
    });
    setBusy(false);
    if (error || data?.error || !data?.conversation_id) {
      setMessages((current) => current.filter((item) => item.id !== optimistic.id));
      setDraft(text);
      setNotice(String(data?.error ?? error?.message ?? "RUNYI Bot failed."));
      return;
    }
    const id = String(data.conversation_id);
    setConversationId(id);
    await loadMessages(id);
    await loadHistory();
    await loadStatistics();
  }

  async function saveModel(providerKey: string, value: string) {
    if (!supabase || !isAdmin || !botKey) return;
    setBusy(true);
    setNotice(null);
    const automatic = value === AUTO_MODEL;
    const { data, error } = await supabase.functions.invoke<Row>("runyi-bot-chat", {
      body: {
        action: "save_bot",
        operation: "UPDATE",
        bot_key: botKey,
        patch: {
          provider_key: providerKey,
          model_key: automatic ? null : value,
          model_policy: automatic ? "CHEAPEST_ELIGIBLE_MODEL" : "EXPLICIT_MODEL",
        },
      },
    });
    setBusy(false);
    if (error || data?.error) {
      setNotice(String(data?.error ?? error?.message));
      return;
    }
    acceptCatalogue(data?.catalogue, botKey);
  }

  async function deleteConversation(item: Conversation) {
    if (!supabase || !isAdmin || !botKey) return;
    const { data, error } = await supabase.functions.invoke<Row>("runyi-bot-chat", {
      body: { action: "delete_conversation", bot_key: botKey, conversation_id: item.conversation_id },
    });
    if (error || data?.error) {
      setNotice(String(data?.error ?? error?.message));
      return;
    }
    if (conversationId === item.conversation_id) newChat();
    setRowMenu(null);
    await loadHistory();
  }

  async function retireBot(targetBot: string) {
    if (!supabase || !isAdmin) return;
    setBusy(true);
    const { data, error } = await supabase.functions.invoke<Row>("runyi-bot-chat", {
      body: { action: "retire_bot", bot_key: targetBot },
    });
    setBusy(false);
    if (error || data?.error) {
      setNotice(String(data?.error ?? error?.message));
      return;
    }
    acceptCatalogue(data?.catalogue);
    setRowMenu(null);
    await loadStatistics();
  }

  if (!connected || !supabase) {
    return <div className="grid h-full place-items-center px-5 text-center text-sm text-foreground/45">Connect RUNYI before using RUNYI BOTS.</div>;
  }

  if (!bot) {
    return (
      <div className="grid h-full place-items-center text-center">
        <div>
          <Bot className="mx-auto size-8 text-foreground/35" />
          <p className="mt-3 text-sm font-semibold">No active RUNYI Bot</p>
          {isAdmin ? (
            <Button className="mt-4 rounded-full" onClick={() => setCreateBotOpen(true)}>
              <Plus className="mr-2 size-4" /> New Bot
            </Button>
          ) : null}
        </div>
        {createBotOpen ? (
          <BotConfig supabase={supabase} catalogue={catalogue} selected="" forceNew close={() => setCreateBotOpen(false)} update={acceptCatalogue} />
        ) : null}
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden overscroll-none bg-background">
      <header className="sticky top-0 z-30 shrink-0 border-b border-foreground/8 bg-background/95 px-2 py-2 backdrop-blur-xl">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="size-9 rounded-full" onClick={() => setDrawerOpen(true)} aria-label="Open RUNYI Bots drawer">
            <Menu className="size-5" />
          </Button>
          <label className="relative min-w-0 flex-1">
            <Bot className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-primary" />
            <select
              value={botKey}
              onChange={(event) => setBotKey(event.target.value)}
              className="h-9 w-full appearance-none rounded-xl border border-foreground/10 bg-foreground/[0.035] pl-9 pr-8 text-xs font-semibold outline-none"
              aria-label="RUNYI Bot"
            >
              {bots.filter((item) => item.active !== false).map((item) => (
                <option key={item.bot_key} value={item.bot_key}>{item.display_name ?? item.bot_key}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground/40" />
          </label>
          <Button variant={surface === "statistics" ? "secondary" : "ghost"} size="sm" className="h-9 rounded-xl px-2.5 text-[10px]" onClick={() => { setSurface("statistics"); void loadStatistics(); }}>
            <BarChart3 className="mr-1 size-3.5" /> Statistics
          </Button>
          {isAdmin ? (
            <Button variant={surface === "manage" ? "secondary" : "ghost"} size="icon" className="size-9 rounded-full" onClick={() => { setSurface("manage"); setConfigOpen(true); }} aria-label="Manage Bot">
              <Settings2 className="size-4" />
            </Button>
          ) : null}
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <label>
            <span className="mb-1 block px-1 text-[8px] font-semibold uppercase tracking-[0.08em] text-foreground/35">Provider</span>
            <select value={provider} disabled={!isAdmin || busy} onChange={(event) => void saveModel(event.target.value, AUTO_MODEL)} className="h-9 w-full rounded-xl border border-foreground/10 bg-foreground/[0.03] px-2 text-[11px]">
              {providers.map((item) => <option key={item.provider_key} value={item.provider_key}>{item.provider_name ?? item.provider_key}</option>)}
            </select>
          </label>
          <label>
            <span className="mb-1 block px-1 text-[8px] font-semibold uppercase tracking-[0.08em] text-foreground/35">Model</span>
            <select value={modelValue} disabled={!isAdmin || busy} onChange={(event) => void saveModel(provider, event.target.value)} className="h-9 w-full rounded-xl border border-foreground/10 bg-foreground/[0.03] px-2 text-[11px]">
              <option value={AUTO_MODEL}>Automatic · cheapest eligible</option>
              {providerModels.map((item) => <option key={item.model_key} value={item.model_key}>{item.display_name ?? item.model_key}</option>)}
            </select>
          </label>
        </div>
        <p className="mt-1.5 px-1 text-[9px] text-foreground/38">
          {automaticModel ? `Automatic → ${bot.effective_model_key ?? "resolving"}` : `${provider} · ${bot.model_key}`}
        </p>
      </header>

      {surface === "statistics" ? (
        <BotStatistics statistics={statistics} selected={selectedStats} scope={statsScope} setScope={setStatsScope} close={() => setSurface("conversation")} />
      ) : (
        <>
          <main className="min-h-0 flex-1 overscroll-contain overflow-y-auto px-5 py-6 sm:px-10">
            {messages.length === 0 ? (
              <div className="mx-auto flex min-h-[45vh] max-w-xl flex-col items-center justify-center text-center">
                <Bot className="size-8 text-primary" />
                <h2 className="mt-3 text-lg font-semibold">{bot.display_name}</h2>
                <p className="mt-1 text-xs leading-5 text-foreground/45">{bot.purpose}</p>
                <p className="mt-3 rounded-full bg-foreground/[0.045] px-3 py-1 text-[9px] text-foreground/45">Dedicated Bot history · separate from RUNYI AI</p>
              </div>
            ) : (
              <div className="mx-auto max-w-3xl space-y-7">
                {messages.map((message) => (
                  <div key={message.id} className={message.role === "user" ? "flex justify-end" : "flex justify-start"}>
                    <div className={message.role === "user" ? "max-w-[86%] rounded-2xl rounded-br-md bg-foreground px-4 py-3 text-sm leading-6 text-background" : "max-w-[92%] whitespace-pre-wrap text-sm leading-6 text-foreground/82"}>
                      {stripBotProfile(message.content, message.role)}
                    </div>
                  </div>
                ))}
                {busy ? <div className="flex items-center gap-2 text-xs text-foreground/42"><LoaderCircle className="size-4 animate-spin" />{bot.display_name} is working…</div> : null}
              </div>
            )}
          </main>
          <div className="sticky bottom-0 z-30 shrink-0 border-t border-foreground/8 bg-background/95 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-2 backdrop-blur-xl sm:px-8 sm:pb-5">
            <div className="mx-auto max-w-3xl">
              {notice ? <div className="mb-2 rounded-xl bg-amber-400/10 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-200">{notice}</div> : null}
              <div className="rounded-[24px] bg-foreground/[0.05] p-1.5">
                <Textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      void send();
                    }
                  }}
                  disabled={busy}
                  rows={1}
                  placeholder={`Message ${bot.display_name}`}
                  className="max-h-32 min-h-[42px] resize-none border-0 bg-transparent px-3 py-2 text-[16px] shadow-none focus-visible:ring-0"
                />
                <div className="flex justify-end px-1 pb-0.5">
                  <Button size="icon" className="size-9 rounded-full bg-foreground text-background" onClick={() => void send()} disabled={busy || !draft.trim()}>
                    {busy ? <LoaderCircle className="size-4 animate-spin" /> : <Send className="size-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {drawerOpen ? (
        <div className="absolute inset-0 z-[100]">
          <button className="absolute inset-0 bg-black/45" onClick={() => { setDrawerOpen(false); setRowMenu(null); }} aria-label="Close drawer" />
          <aside className="absolute inset-y-0 left-0 flex w-[86%] max-w-sm flex-col bg-background shadow-2xl">
            <div className="flex items-center justify-between p-3">
              <div>
                <p className="text-sm font-semibold">RUNYI BOTS</p>
                <p className="text-[9px] text-foreground/38">Bots only · conversational + manual</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setDrawerOpen(false)}><X className="size-4" /></Button>
            </div>
            {isAdmin ? (
              <div className="px-3">
                <Button className="w-full justify-start rounded-xl" onClick={() => { setCreateBotOpen(true); setRowMenu(null); }}>
                  <Plus className="mr-2 size-4" /> New Bot
                </Button>
              </div>
            ) : null}
            <div className="mt-4 px-3 text-[9px] font-semibold uppercase tracking-[0.12em] text-foreground/35">Your Bots</div>
            <div className="mt-1 max-h-[36vh] overflow-y-auto px-2">
              {bots.filter((item) => item.active !== false).map((item) => (
                <div key={item.bot_key} className="relative flex items-center gap-1 rounded-xl hover:bg-foreground/[0.05]">
                  <button className="min-w-0 flex-1 px-3 py-2.5 text-left" onClick={() => { setBotKey(String(item.bot_key)); setDrawerOpen(false); setRowMenu(null); }}>
                    <span className="block truncate text-xs font-medium">{item.display_name ?? item.bot_key}</span>
                    <span className="block truncate text-[9px] text-foreground/35">{item.effective_provider_key ?? item.provider_key} · {item.effective_model_key ?? "Automatic"}</span>
                  </button>
                  <button className="mr-1 grid size-8 place-items-center rounded-lg text-foreground/45 hover:bg-foreground/[0.07]" onClick={() => setRowMenu((current) => current === `bot:${item.bot_key}` ? null : `bot:${item.bot_key}`)} aria-label="Bot menu">
                    <MoreVertical className="size-4" />
                  </button>
                  {rowMenu === `bot:${item.bot_key}` ? (
                    <div className="absolute right-1 top-10 z-20 w-36 rounded-xl border border-foreground/10 bg-background p-1 shadow-xl">
                      <button className="w-full rounded-lg px-3 py-2 text-left text-[10px] hover:bg-foreground/[0.05]" onClick={() => { setBotKey(String(item.bot_key)); setDrawerOpen(false); setConfigOpen(true); setSurface("manage"); setRowMenu(null); }}>Manage</button>
                      <button className="w-full rounded-lg px-3 py-2 text-left text-[10px] hover:bg-foreground/[0.05]" onClick={() => { setBotKey(String(item.bot_key)); setDrawerOpen(false); setSurface("statistics"); setStatsScope("SELECTED_BOT"); setRowMenu(null); void loadStatistics(); }}>Statistics</button>
                      {isAdmin ? <button className="w-full rounded-lg px-3 py-2 text-left text-[10px] text-destructive hover:bg-destructive/10" onClick={() => void retireBot(String(item.bot_key))}>Retire</button> : null}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="mt-3 border-t border-foreground/8 px-3 pt-3">
              <Button variant="secondary" className="w-full justify-start rounded-xl" onClick={newChat}><Plus className="mr-2 size-4" /> New chat</Button>
              <p className="mt-3 text-[9px] font-semibold uppercase tracking-[0.12em] text-foreground/35">Recent for {bot.display_name}</p>
            </div>
            <div className="mt-1 min-h-0 flex-1 overflow-y-auto px-2 pb-4">
              {conversations.length === 0 ? <p className="px-3 py-8 text-center text-xs text-foreground/35">No conversations for this Bot yet.</p> : null}
              {conversations.map((conversation) => (
                <div key={conversation.conversation_id} className="relative flex items-center gap-1 rounded-xl hover:bg-foreground/[0.05]">
                  <button className="min-w-0 flex-1 px-3 py-3 text-left" onClick={() => { setConversationId(conversation.conversation_id); setDrawerOpen(false); setRowMenu(null); setSurface("conversation"); }}>
                    <span className="block truncate text-xs">{conversation.title}</span>
                  </button>
                  <button className="mr-1 grid size-8 place-items-center rounded-lg text-foreground/45 hover:bg-foreground/[0.07]" onClick={() => setRowMenu((current) => current === `chat:${conversation.conversation_id}` ? null : `chat:${conversation.conversation_id}`)} aria-label="Conversation menu">
                    <MoreVertical className="size-4" />
                  </button>
                  {rowMenu === `chat:${conversation.conversation_id}` ? (
                    <div className="absolute right-1 top-10 z-20 w-32 rounded-xl border border-foreground/10 bg-background p-1 shadow-xl">
                      {isAdmin ? (
                        <button className="flex w-full items-center rounded-lg px-3 py-2 text-left text-[10px] text-destructive hover:bg-destructive/10" onClick={() => void deleteConversation(conversation)}>
                          <Trash2 className="mr-2 size-3.5" /> Delete
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </aside>
        </div>
      ) : null}

      {configOpen ? (
        <BotConfig supabase={supabase} catalogue={catalogue} selected={botKey} close={() => { setConfigOpen(false); setSurface("conversation"); }} update={(next, preferred) => { acceptCatalogue(next, preferred); void loadStatistics(); }} />
      ) : null}
      {createBotOpen ? (
        <BotConfig supabase={supabase} catalogue={catalogue} selected="" forceNew close={() => setCreateBotOpen(false)} update={(next, preferred) => { acceptCatalogue(next, preferred); setCreateBotOpen(false); void loadStatistics(); }} />
      ) : null}
    </div>
  );
}

function BotStatistics({
  statistics,
  selected,
  scope,
  setScope,
  close,
}: {
  statistics: Row | null;
  selected: Row | null;
  scope: StatsScope;
  setScope: (scope: StatsScope) => void;
  close: () => void;
}) {
  const summary = statistics?.summary ?? {};
  const cards = scope === "SELECTED_BOT" && selected
    ? [
        ["Tasks", selected.tasks?.total ?? 0],
        ["Succeeded", selected.tasks?.succeeded ?? 0],
        ["Recovery", selected.tasks?.recovery_required ?? 0],
        ["Agents", selected.agents?.distinct_agents_used ?? 0],
        ["AI calls", selected.ai_usage?.calls ?? 0],
        ["Tool calls", selected.tools?.calls ?? 0],
        ["Cost", formatMoney(selected.cost?.estimated_ai_usd)],
        ["Success", selected.performance?.success_rate_pct == null ? "—" : `${selected.performance.success_rate_pct}%`],
      ]
    : [
        ["Bots", summary.bots ?? 0],
        ["Tasks", summary.tasks ?? 0],
        ["Succeeded", summary.succeeded ?? 0],
        ["Recovery", summary.recovery_required ?? 0],
        ["AI calls", summary.ai_calls ?? 0],
        ["Tool calls", summary.tool_calls ?? 0],
        ["Cost", formatMoney(summary.estimated_ai_cost_usd)],
        ["Running", summary.running ?? 0],
      ];
  const detailBots = scope === "SELECTED_BOT" && selected ? [selected] : (Array.isArray(statistics?.bots) ? statistics!.bots as Row[] : []);
  return (
    <main className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between gap-3">
          <div><h2 className="text-lg font-semibold">Bot Statistics</h2><p className="text-[10px] text-foreground/40">Canonical RUNYI execution provenance and usage ledgers</p></div>
          <Button variant="ghost" size="icon" onClick={close}><X className="size-4" /></Button>
        </div>
        <div className="mt-4 flex gap-2">
          <Button size="sm" variant={scope === "SELECTED_BOT" ? "default" : "secondary"} onClick={() => setScope("SELECTED_BOT")}>Selected Bot</Button>
          <Button size="sm" variant={scope === "ALL_BOTS" ? "default" : "secondary"} onClick={() => setScope("ALL_BOTS")}>All Bots</Button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {cards.map(([label, value]) => <div key={String(label)} className="rounded-2xl bg-foreground/[0.04] p-3"><p className="text-[9px] uppercase tracking-wide text-foreground/35">{label}</p><p className="mt-1 text-lg font-semibold">{String(value)}</p></div>)}
        </div>
        <div className="mt-5 space-y-3">
          {detailBots.map((item) => (
            <section key={item.bot_key} className="rounded-2xl border border-foreground/8 p-4">
              <div className="flex items-center justify-between"><div><p className="text-sm font-semibold">{item.display_name ?? item.bot_key}</p><p className="text-[10px] text-foreground/40">{item.bot_key}</p></div><p className="text-sm font-semibold">{formatMoney(item.cost?.estimated_ai_usd)}</p></div>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <div><p className="text-[9px] font-semibold uppercase text-foreground/35">Agents used</p>{(item.agents?.breakdown ?? []).map((agent: Row) => <p key={agent.agent_key} className="mt-1 text-[11px]">{agent.agent_key} · {agent.sessions} sessions</p>)}</div>
                <div><p className="text-[9px] font-semibold uppercase text-foreground/35">Models</p>{(item.ai_usage?.models ?? []).map((model: Row) => <p key={`${model.provider_key}:${model.model_key}`} className="mt-1 text-[11px]">{model.provider_key} · {model.model_key} · {model.calls} calls</p>)}</div>
                <div><p className="text-[9px] font-semibold uppercase text-foreground/35">Tools</p>{(item.tools?.breakdown ?? []).map((tool: Row) => <p key={tool.tool_key} className="mt-1 text-[11px]">{tool.tool_key} · {tool.calls} calls</p>)}</div>
              </div>
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}

function BotConfig({
  supabase,
  catalogue,
  selected,
  forceNew = false,
  close,
  update,
}: {
  supabase: SupabaseClient;
  catalogue: Row | null;
  selected: string;
  forceNew?: boolean;
  close: () => void;
  update: (catalogue: Row, preferred?: string) => void;
}) {
  const current = (Array.isArray(catalogue?.bots) ? catalogue!.bots as Row[] : []).find((item) => item.bot_key === selected) ?? null;
  const [newMode, setNewMode] = useState(forceNew || !current);
  const [instruction, setInstruction] = useState("");
  const [key, setKey] = useState("");
  const [displayName, setDisplayName] = useState(String(current?.display_name ?? ""));
  const [purpose, setPurpose] = useState(String(current?.purpose ?? ""));
  const [instructions, setInstructions] = useState(String(current?.instructions ?? ""));
  const [risk, setRisk] = useState(String(current?.risk_ceiling ?? "MEDIUM"));
  const [ownerOnly, setOwnerOnly] = useState(Boolean(current?.owner_only ?? false));
  const [prefixes, setPrefixes] = useState(Array.isArray(current?.allowed_action_prefixes) ? current.allowed_action_prefixes.join(", ") : "");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function conversational() {
    if (!instruction.trim()) return;
    setBusy(true);
    const { data, error } = await supabase.functions.invoke<Row>("runyi-bot-chat", {
      body: { action: "configure_conversational", instruction: instruction.trim(), bot_key: newMode ? undefined : selected },
    });
    setBusy(false);
    if (error || data?.error) { setNotice(String(data?.error ?? error?.message)); return; }
    if (data?.catalogue) update(data.catalogue, String(data?.composed?.bot_key ?? selected));
    setInstruction("");
    setNotice("Bot configuration updated conversationally.");
  }

  async function manual() {
    const botKey = newMode ? key.trim().toUpperCase().replace(/[^A-Z0-9_]+/g, "_") : selected;
    if (!botKey) { setNotice("Bot key required."); return; }
    const patch = {
      display_name: displayName.trim() || botKey,
      purpose: purpose.trim() || "RUNYI governed Bot.",
      instructions,
      risk_ceiling: risk,
      owner_only: ownerOnly,
      allowed_action_prefixes: prefixes.split(",").map((item) => item.trim()).filter(Boolean),
      active: true,
    };
    setBusy(true);
    const { data, error } = await supabase.functions.invoke<Row>("runyi-bot-chat", {
      body: { action: "save_bot", operation: newMode ? "CREATE" : "UPDATE", bot_key: botKey, patch },
    });
    setBusy(false);
    if (error || data?.error) { setNotice(String(data?.error ?? error?.message)); return; }
    if (data?.catalogue) update(data.catalogue, botKey);
    setNotice("Bot configuration saved manually.");
  }

  return (
    <div className="absolute inset-0 z-[130]">
      <button className="absolute inset-0 bg-black/55" onClick={close} aria-label="Close Bot configuration" />
      <section className="absolute inset-x-2 bottom-2 top-8 overflow-hidden rounded-2xl border border-foreground/10 bg-background shadow-2xl sm:left-auto sm:w-[32rem]">
        <div className="flex items-center justify-between border-b border-foreground/8 px-4 py-3">
          <div><p className="text-sm font-semibold">{newMode ? "New Bot" : "Manage Bot"}</p><p className="text-[10px] text-foreground/40">Manual + conversational · one canonical Bot object</p></div>
          <Button variant="ghost" size="icon" onClick={close}><X className="size-4" /></Button>
        </div>
        <div className="h-[calc(100%-4rem)] overflow-y-auto p-4">
          {current && !forceNew ? (
            <div className="mb-4 flex gap-1"><button className={`rounded-full px-3 py-1.5 text-[10px] ${!newMode ? "bg-foreground text-background" : "bg-foreground/[0.05]"}`} onClick={() => setNewMode(false)}>Current Bot</button><button className={`rounded-full px-3 py-1.5 text-[10px] ${newMode ? "bg-foreground text-background" : "bg-foreground/[0.05]"}`} onClick={() => setNewMode(true)}>New Bot</button></div>
          ) : null}
          <Textarea value={instruction} onChange={(event) => setInstruction(event.target.value)} placeholder='“Use the cheapest OpenAI model” or “Create a deployment Bot using Grok”' className="min-h-20" />
          <Button className="mt-2 w-full rounded-xl" disabled={busy || !instruction.trim()} onClick={() => void conversational()}>Apply conversationally</Button>
          <div className="my-4 h-px bg-foreground/8" />
          {newMode ? <label className="block"><span className="text-[9px] font-semibold uppercase text-foreground/35">Bot key</span><input value={key} onChange={(event) => setKey(event.target.value.toUpperCase())} placeholder="MY_RUNYI_BOT" className="mt-1 h-10 w-full rounded-xl border border-foreground/10 bg-foreground/[0.025] px-3 text-sm outline-none" /></label> : null}
          <label className="mt-3 block"><span className="text-[9px] font-semibold uppercase text-foreground/35">Name</span><input value={displayName} onChange={(event) => setDisplayName(event.target.value)} className="mt-1 h-10 w-full rounded-xl border border-foreground/10 bg-foreground/[0.025] px-3 text-sm outline-none" /></label>
          <label className="mt-3 block"><span className="text-[9px] font-semibold uppercase text-foreground/35">Purpose</span><Textarea value={purpose} onChange={(event) => setPurpose(event.target.value)} className="mt-1 min-h-20" /></label>
          <label className="mt-3 block"><span className="text-[9px] font-semibold uppercase text-foreground/35">Instructions</span><Textarea value={instructions} onChange={(event) => setInstructions(event.target.value)} className="mt-1 min-h-28" /></label>
          <div className="mt-3 grid grid-cols-2 gap-2"><label><span className="text-[9px] font-semibold uppercase text-foreground/35">Risk ceiling</span><select value={risk} onChange={(event) => setRisk(event.target.value)} className="mt-1 h-10 w-full rounded-xl border border-foreground/10 bg-background px-3 text-sm"><option>LOW</option><option>MEDIUM</option><option>HIGH</option><option>CRITICAL</option></select></label><label className="flex items-end gap-2 pb-2 text-xs"><input type="checkbox" checked={ownerOnly} onChange={(event) => setOwnerOnly(event.target.checked)} /> Owner only</label></div>
          <label className="mt-3 block"><span className="text-[9px] font-semibold uppercase text-foreground/35">Allowed action prefixes</span><input value={prefixes} onChange={(event) => setPrefixes(event.target.value)} placeholder="enterprise., github." className="mt-1 h-10 w-full rounded-xl border border-foreground/10 bg-foreground/[0.025] px-3 text-sm outline-none" /></label>
          {notice ? <div className="mt-3 rounded-xl bg-amber-400/10 px-3 py-2 text-[11px]">{notice}</div> : null}
          <Button className="mt-4 w-full rounded-xl" disabled={busy} onClick={() => void manual()}>Save manually</Button>
        </div>
      </section>
    </div>
  );
}
